import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { AdminApiService } from 'app/services/admin-api.service';
import { ModalComponent } from 'app/shared/modal/modal.component';
import { ModalService } from 'app/shared/modal/modal.service';
import CustomValidators, { emailWithTextAppendedValidator } from 'app/shared/custom-validators';
import { RegionDto, RoleDto, SessionUser, UserDto } from '@system-shared/classes.dto';
import { forkJoin, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { RoleEnum } from "@system-shared/enums";
import { LocationStrategy } from "@angular/common";

@Component({
    selector: 'app-admin-manage-users',
    templateUrl: './manage-users.component.html',
    styleUrls: ['./manage-users.scss'],
    standalone: false
})
export class AdminManageUsersComponent implements OnInit, OnDestroy {
  @ViewChild('modal', { read: ViewContainerRef }) modal: ModalComponent;
  userForm: FormGroup;
  emailForm: FormGroup;
  roleFormValid = false;

  allRoles = ['Regional Admin', 'Forest Admin', 'Permit Manager', 'Permit Viewer'];
  allRegions: RegionDto[] = [];
  selectedRegions: RegionDto[] = [];
  selectedRegionRoleForests: RegionDto[] = [];
  addUser: boolean = false;
  editUser: boolean = false;
  viewUser: boolean = false;
  activeRegion = null;
  userId = null;
  user: UserDto = null;
  hasLoaded: boolean = false;
  isUserNationalAdmin: boolean = false;
  isUserRegionalAdmin: boolean = false;
  hasUserSearched: boolean = false;
  spinner: boolean = false;

  sessionUser: SessionUser;
  isUpdated: boolean;
  updatedAction: string;
  showModal: boolean = false;
  modalConfig: { closeButtonText: string; title: string; body: string; confirmButtonText: string };
  loadedSelectedRegionRoleForests: RegionDto[] = [];

  isFormSaved: boolean = false;
  navigationUrl: string;
  canUserDeactivate = false;
  routerSub;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly adminApiService: AdminApiService,
    private readonly modalService: ModalService,
    private readonly locationStrategy: LocationStrategy,
  ) {
    this.sessionUser = this.route.snapshot.data.sessionUser;
    this.route.queryParams.subscribe(params => {
      if (params['updated'] && ((Number(params['updated']) + 2000) > new Date().getTime())) {
        this.isUpdated = true;
      }
    });

    // Prevent back Navigation on Dirty
    this.locationStrategy.onPopState(()=>{
      if (this.isDirty()) {
        this.showConfirmModal();
        this.locationStrategy.pushState(null, '', this.locationStrategy.path(), '');
        return false;
      }
      return true;
    });
  }

  canDeactivate(): boolean | Promise<boolean> | Observable<boolean> {
    // Verify Editing && Check if dirty
    if ((this.editUser && this.isDirty()) || (this.addUser && !this.isFormSaved)) {
      this.showConfirmModal();
      return this.canUserDeactivate;
    }
    return true;
  }

  isDirty(): boolean {
    const statusUpdated = this.user?.status.toLowerCase() !== ((this.userForm?.controls.status.value) ? 'active' : 'inactive');
    const forestUpdated = JSON.stringify(this.loadedSelectedRegionRoleForests) !== JSON.stringify(this.selectedRegionRoleForests);
    const phoneNumberUpdated = (this.user?.phoneNumber || '') !== (this.userForm?.controls.phone_number.value || '');

    if (this.editUser) {
      return !this.isFormSaved && (statusUpdated || forestUpdated || phoneNumberUpdated);
    } else if (this.addUser) {
      return !this.isFormSaved && (statusUpdated && forestUpdated);
    }
  }

  ngOnInit() {
    this.initForm();
    forkJoin([this.route.params.pipe(take(1)), this.route.data.pipe(take(1))])
    .subscribe((res) => {
      const params = res[0];
      const data = res[1];

      this.processUrlParams(params);
      this.processRouteData(data);

      if (this.editUser || this.viewUser) {
        this.getUser();
        this.disableControls();
      }
    });
    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationUrl = event.url;
      }
    });
  };

  ngOnDestroy(): void {
    this.routerSub.unsubscribe();
  }

  processRouteData(data) {
    const r = data.forests.sort((a, b) => {
      if (a.forestCode > b.forestCode) {
        return 1;
      } else if (b.forestCode > a.forestCode) {
        return -1;
      } else {
        return  0;
      }
    });
    const regions = r.reduce((x, y) => {
      (x[y.regionCode] = x[y.regionCode] || {
        regionCode: y.regionCode,
        regionName: y.region,
        forests: []
      });
      x[y.regionCode].forests.push(y);
      return x;
    }, {});
    this.allRegions = Object.values(regions);
    this.allRegions.sort((a, b) => {
      if (a.regionCode > b.regionCode) {
        return 1;
      } else if (b.regionCode > a.regionCode) {
        return -1;
      } else {
        return  0;
      }
    });
  }

  processUrlParams(params) {
    if (this.router.url === '/admin/users/add') {
      this.addUser = true;
      this.hasLoaded = true;
    } else if (!isNaN(params.id)) {
      if (this.router.url.endsWith('edit')) {
        this.editUser = true;
      } else {
        this.viewUser = true;
      }
      this.userId = params.id;
    }
  }

  initForm() {
    this.userForm = this.formBuilder.group({
      status: [true, Validators.required],
      phone_number: ['', [Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]]
    });
    this.emailForm = this.formBuilder.group({
      usda_email_address: ['', [Validators.required, Validators.maxLength(50), emailWithTextAppendedValidator('@usda.gov')]]
    });
    this.emailForm.get('usda_email_address').valueChanges.subscribe(event => {
      this.emailForm.get('usda_email_address').setValue(event.toLowerCase(), {emitEvent: false})
    })
  };

  disableControls() {
    if (this.viewUser) {
      this.userForm.controls.status.disable();
      this.userForm.controls.phone_number.disable();
    }
  }

  getUser() {
    this.adminApiService.getUser(this.userId).subscribe((user) => {
      this.user = user;
      this.isUserNationalAdmin = user.roles.some(role => role.code === 'NATIONAL_ADMIN');
      this.isUserRegionalAdmin = user.roles.some(role => role.code === 'REGIONAL_ADMIN');
      this.hasLoaded = true;
      this.patchUser();
    })
  };

  patchUser() {
    this.userForm.controls.phone_number.setValue(this.user.phoneNumber);
    if (this.user.status === 'Active') {
      this.userForm.controls.status.setValue(true);
    } else if (this.user.status === 'Inactive') {
      this.userForm.controls.status.setValue(false);
    };
    // ADD CODE TO PATCH ROLES, REGIONS, & FORESTS
    // Patch Regions
    this.allRegions.filter(x => this.user.regions.includes(x.regionCode))
    .forEach(region => this.selectRegion(true, region));

    // Create Map To Associate Selected Forest To Forest Roles
    const regionForestsMap: Map<String, String[]> = new Map<String, String[]>();
    this.user.roles.forEach(role => {
      if (!regionForestsMap.has(role.regionCode)) {
        regionForestsMap.set(role.regionCode, []);
      }
      regionForestsMap.get(role.regionCode).push(role.forestCode);
    })

    // Load Forest to Selected Regions
    this.selectedRegionRoleForests.forEach((region) => {
      // Set Selected Forest if any
      region.forests = region.allForests.filter(forest => regionForestsMap.get(region.regionCode).includes(String(forest.code)));
      // Set Assigned Role For Region if available
      region.role = this.user.roles.find(x => x.regionCode === region.regionCode)?.name;
    });
    // Create Loaded State To Compare For Changes
    this.loadedSelectedRegionRoleForests = JSON.parse(JSON.stringify(this.selectedRegionRoleForests));

    // Check If SessionUser Can Modify User Status
    // If User is National Admin and the SessionUser Is Not Then Disable
    const bothNationalAdmin = this.sessionUser.isNationalAdmin && this.user.isNationalAdmin;
    const belongsToMultipleRegions = regionForestsMap.size > 1;

    if (this.sessionUser.isNationalAdmin || this.sessionUser.isRegionalAdmin && this.user)

    this.getSessionUserRegionRoleMap().values()
    this.hasLoaded = true;
  };

  saveUpdates() {
    let status = '';
    if (this.userForm.controls.status.value) { status = 'Active'} else { status = 'Inactive'};

    const userDto = new UserDto({});
    userDto.email = this.emailForm.getRawValue().usda_email_address;
    userDto.status = status;
    userDto.phoneNumber = this.userForm.getRawValue().phone_number;
    userDto.isNationalAdmin = this.isUserNationalAdmin;
    if (!userDto.isNationalAdmin) {
      const roles = [];
      this.selectedRegionRoleForests.forEach((region: RegionDto) => {
        region.forests.forEach((forest) => {
          const role = new RoleDto({});
          role.code = region.role;
          role.regionCode = region.regionCode;
          role.forestCode = forest.code;
          roles.push(role);
        })
      });
      userDto.roles = roles;
    }
    if (this.addUser) {
      this.adminApiService.postUser(userDto).subscribe((res) => { //TODO: Add Error Handling
        this.isFormSaved = true;
        this.canUserDeactivate = true;
        this.router.navigate(['/admin/users'], {queryParams: { updated: new Date().getTime(), action: 'addUser' }});
      });
    } else if (this.editUser) {
      userDto.email = this.user.email;
      userDto.id = this.user.id;
      this.adminApiService.updateUser(userDto).subscribe((res) => {
        this.isFormSaved = true;
        this.canUserDeactivate = true;
        this.router.navigate([`/admin/users`], {queryParams: { updated: new Date().getTime(), action: 'editUser' }});
      });
    }
  };

  changeTab(region) {
    this.activeRegion = region;
  }

  toggleEditMode() {
    this.router.navigate([`/admin/users/${this.userId}/edit`]);
  }

  regionChange(e, region: RegionDto) {
    const isChecked = e.target.checked;
    if (isChecked && this.selectedRegionRoleForests.length + 1 === this.allRegions.length) {
      this.showWarningModal();
      e.target.checked = false;
      return;
    }
    this.selectRegion(isChecked, region);
  };

  private selectRegion(selected: boolean, region: RegionDto) {
    if (selected) {
      this.selectedRegions.push(region);
      this.selectedRegionRoleForests.push({
        regionCode: region.regionCode,
        regionName: region.regionName,
        role: null,
        forests: [],
        allForests: region.forests
      });
    } else {
      this.selectedRegions = this.selectedRegions.filter(x => x.regionName !== region.regionName);
      this.selectedRegionRoleForests = this.selectedRegionRoleForests.filter(x => x.regionCode !== region.regionCode);
    }
    this.selectedRegionRoleForests.sort((a, b) => {
      if (a.regionCode > b.regionCode) {
        return 1;
      } else if (b.regionCode > a.regionCode) {
        return -1;
      } else {
        return  0;
      }
    });
    this.activeRegion = this.selectedRegionRoleForests[0];
  }

  roleChange(role) {
    const activeIndex = this.selectedRegionRoleForests.indexOf(this.activeRegion);
    this.selectedRegionRoleForests[activeIndex].role = role;
    this.activeRegion.role = role;
    if (role === 'Regional Admin' || role === 'National Admin') {
      this.selectedRegionRoleForests[activeIndex].forests = this.selectedRegionRoleForests[activeIndex].allForests;
    } else {
      this.selectedRegionRoleForests[activeIndex].forests = [];
    }
  };

  forestChange(e, forest) {
    const activeIndex = this.selectedRegionRoleForests.indexOf(this.activeRegion);
    if (e.target.value === 'all' && e.target.checked) {
      this.selectedRegionRoleForests[activeIndex].forests = this.activeRegion.allForests;
    } else if (e.target.value === 'all' && !e.target.checked) {
      const activeIndex = this.selectedRegionRoleForests.indexOf(this.activeRegion);
      this.selectedRegionRoleForests[activeIndex].forests = [];
    } else {
      if (e.target.checked) {
        const index = this.selectedRegionRoleForests.indexOf(this.activeRegion);
        this.selectedRegionRoleForests[index].forests.push(forest);
      } else {
        const removeIndex = this.selectedRegionRoleForests.indexOf(this.activeRegion);
        this.selectedRegionRoleForests[removeIndex].forests = this.selectedRegionRoleForests[removeIndex].forests.filter(f => {return f.code !== forest.code});
      }
    }
    if (this.selectedRegionRoleForests[activeIndex].forests.length === this.selectedRegionRoleForests[activeIndex].allForests.length && this.selectedRegionRoleForests[activeIndex].allForests.length > 1 && this.selectedRegionRoleForests[activeIndex].role === 'Forest Admin') {
      this.showAllForestsWarningModal();
    }
  };

  checkIfRoleFormValid() {
    if (this.isUserNationalAdmin) {
      return true;
    } else if (this.selectedRegionRoleForests.length > 0) {
      return this.selectedRegionRoleForests.every((r) => {
        if(r.role === 'Regional Admin') {
          return true;
        } else if ((r.role === 'Forest Admin') && this.selectedRegionRoleForests.length !== this.allRegions.length && ((r.forests.length > 0 && r.forests.length < r.allForests.length) || (r.allForests.length === 1 && r.forests.length === 1))) {
          return true;
        } else if ((r.role === 'Permit Manager' || r.role === 'Permit Viewer') && this.selectedRegionRoleForests.length !== this.allRegions.length && r.forests.length > 0) {
          return true;
        }
      })
    } else return this.selectedRegionRoleForests.length === 0 && this.isDirty();
  }

  numberOfTabs() {
    const numberOfTabs = Math.ceil(this.selectedRegionRoleForests.length / 4) * 4;
    return numberOfTabs;
  }

  changeNationalAdmin(e) {
    this.isUserNationalAdmin = e.target.checked;
    this.selectedRegions = [];
    if (e.target.checked) {
      this.allRegions.forEach((region: RegionDto) => {
        this.selectRegion(true, region);
      })
      this.selectedRegionRoleForests.forEach((region: RegionDto) => {
        region.forests = region.allForests;
      })
    } else {
      this.selectedRegionRoleForests = [];
      this.activeRegion = null;
    }
  }

  searchForUser() {
    this.spinner = true;
    this.adminApiService.getUserByEmail(this.getEmailAddressFromForm()).subscribe({
      next: (res: UserDto) => {
        this.user = res;
        this.emailForm.controls.usda_email_address.setValue(this.getEmailAddressFromForm());
        this.emailForm.controls.usda_email_address.disable();
        this.hasUserSearched = true;
        this.addUser = false;
        this.editUser = true;
        this.spinner = false;
        this.isUserNationalAdmin = res.roles.some(role => role.code === 'NATIONAL_ADMIN');
        this.isUserRegionalAdmin = res.roles.some(role => role.code === 'REGIONAL_ADMIN');
        this.hasLoaded = true;
        this.patchUser();
      }, error: (err) => {
        this.emailForm.controls.usda_email_address.setValue(this.getEmailAddressFromForm());

        this.emailForm.controls.usda_email_address.disable();
        this.addUser = true;
        this.hasUserSearched = true;
        this.spinner = false;
      }
    })
  }

  getEmailAddressFromForm(): string {
    let modifiedEmail = this.emailForm.controls.usda_email_address.getRawValue().toLowerCase();
     //checking if email has '@usda.gov' already appended
    if (!modifiedEmail.includes('@usda.gov')){
      modifiedEmail = modifiedEmail.concat('@usda.gov');
    }
    return modifiedEmail;
  }

  isRegionSelected(region: RegionDto): boolean {
    if (this.isUserNationalAdmin) {
      return true;
    }
     return this.selectedRegions.some(selectedRegion => region.regionCode === selectedRegion.regionCode);
  }

  displayRole(region: RegionDto): String {
    if (region.role) return this.convertRoleEnumToTitleCase(region.role);
    if (this.isUserNationalAdmin) {
      return this.convertRoleEnumToTitleCase(RoleEnum.NATIONAL_ADMIN);
    }
    return 'N/A';
  }

  isModifyingSameUser() {
    return !this.addUser && this.user?.id === this.sessionUser?.id;
  }

  showAllForestsWarningModal() {
    this.modalConfig = {
      title: 'Warning!',
      body: 'Forest Admins that are assigned all forests must be changed to a Regional Admin before saving.',
      confirmButtonText: null,
      closeButtonText: 'Close'
    };
    this.openModal();
  }


  showWarningModal() {
    this.modalConfig = {
      title: 'Warning!',
      body: 'Choosing all regions will automatically make this user a National Admin.',
      confirmButtonText: null,
      closeButtonText: 'Close'
    };
    this.openModal();
  }

  showConfirmModal() {
    let modalAction = '';
    if (this.addUser) {
      modalAction = 'Add User'
    } else if (this.editUser) {
      modalAction = 'Edit User'
    }
    this.modalConfig = {
      title: `Cancel ${modalAction}`,
      body: 'This will discard all your current changes. Are you sure you want to cancel?',
      confirmButtonText: 'Yes, Cancel & Exit.',
      closeButtonText: `No, Continue with ${modalAction}.`,
    };
    this.openModal();
  }

  closeModal() {
    this.showModal = false;
  }

  openModal() {
    this.showModal = true;
  }

  confirmModal(event) {
    this.closeModal();
    this.canUserDeactivate = event;
    if (event) {
      this.router.navigate([this.navigationUrl])
    }
  }

  handleCancel() {
    this.router.navigate(['/admin/users'])
  }

  canSessionUserEditRegion(regionCode: string): boolean {
    return this.sessionUser.regionsUserCanEdit?.includes(regionCode);
  }

  canSessionUserEditRegionList(regionList: string[]): boolean {
    return true;
  }

  getAvailableRolesByRegion(regionCode: string) {
    if (this.sessionUser.regionRoleMap) {
      const regionRole = this.getSessionUserRegionRoleMap().get(regionCode);
      return this.rolesEqualToOrBelow(regionRole).map(role => this.convertRoleEnumToTitleCase(role));
    }
    return [];
  }

  private getSessionUserRegionRoleMap() {
    return new Map<string, string>(Object.entries(JSON.parse(this.sessionUser.regionRoleMap)));
  }

  rolesEqualToOrBelow(role: string, isForRoleComparison: boolean = false) {
    const availableRoles = [];
    switch (role) {
      case RoleEnum.SUPPORT_USER:
        if (isForRoleComparison) availableRoles.push(RoleEnum.SUPPORT_USER);
      case RoleEnum.NATIONAL_ADMIN:
        if (isForRoleComparison) availableRoles.push(RoleEnum.NATIONAL_ADMIN);
      case RoleEnum.REGIONAL_ADMIN:
        availableRoles.push(RoleEnum.REGIONAL_ADMIN);
      case RoleEnum.FOREST_ADMIN:
        availableRoles.push(RoleEnum.FOREST_ADMIN);
      case RoleEnum.PERMIT_MANAGER:
        availableRoles.push(RoleEnum.PERMIT_MANAGER);
      case RoleEnum.PERMIT_VIEWER:
        availableRoles.push(RoleEnum.PERMIT_VIEWER);
        break;
    }
    return availableRoles;
  }

  convertRoleEnumToTitleCase(str: string): string {
    return str.replace('_', ' ').split(' ')
      .map(w => w[0].toUpperCase() + w.substring(1).toLowerCase())
      .join(' ');
  }

  reloadPage() {
    window.location.reload();
  }
}
